import React, { useCallback, useMemo } from 'react';
import { FilterButton, PanelHeaderActions, PanelHeaderButtons } from './styles';
import { useDispatch, useSelector } from 'react-redux';
import { useAppContext } from 'src/components/app-context';
import { ButtonType } from '@prisjakt/ui/button/variants';
import LayoutPicker from 'src/components/layout-picker';
import Counter from './components/filter/components/counter';
import CompareProductsToggle from '../../components/compare-products-toggle';
import { toggleFilter } from '../sidebar/actions/filter';
import { categoryLayoutRequest } from './actions/category';
import { SIDEBAR_STATE } from '../sidebar/constants';
import { Color, Variant } from './components/filter/components/counter/types';
import { IconFilter } from '@prisjakt/ui/icon/filter';
import { ButtonElevation, ButtonIcon, ButtonText } from '@prisjakt/ui/button';
import type { AppState } from 'src/reducers';
import { getDropdownSortOptions } from './sort/dropdown-sort-options';
import { getDropdownSortSelected } from './sort/dropdown-sort-selected';
import type { RenderMode } from './types';
import type { PageInfo } from 'src/types';
import type { CompareProductsSelectionType } from 'src/hooks/compare-products-selection';
import {
  Dropdown,
  DropdownContent,
  DropdownToggle,
} from '@prisjakt/ui/dropdown';
import { Direction } from '@prisjakt/ui';
import { DropdownItem } from 'src/components/dropdown-item';

export const CategoryHeaderActions: React.FC<CategoryHeaderActionsProps> = ({
  mode,
  onSortChange,
  pageInfo,
  showHorizontalFilter,
  compareProductSelection,
}) => {
  const dispatch = useDispatch();
  const { t } = useAppContext();
  const sidebarSettings = useSelector((state: AppState) => state.areas.sidebar);
  const categorySettings = useSelector(
    (state: AppState) => state.areas.category
  );
  const { error: filterError, query } = categorySettings.filter;

  const abortProductSelectionFromCompareButton = useCallback(
    () => compareProductSelection.abortProductSelection('compareButton'),
    [compareProductSelection]
  );

  const count = Object.keys(query).length;
  const filterIsOpen =
    sidebarSettings && sidebarSettings.tab === SIDEBAR_STATE.FILTER;

  const dropdownSortOptions = useMemo(
    () => getDropdownSortOptions(pageInfo?.sort, t),
    [pageInfo?.sort, t]
  );

  const dropdownSortSelected = useMemo(
    () => getDropdownSortSelected(pageInfo?.sort, t),
    [t, pageInfo?.sort]
  );
  const filterOnClickHandler = () => dispatch(toggleFilter());
  const layoutOnClickHandler = (m: RenderMode) =>
    dispatch(categoryLayoutRequest(m));

  return (
    <PanelHeaderActions>
      {!filterError && !showHorizontalFilter && (
        <FilterButton
          type={filterIsOpen ? ButtonType.PRIMARY : ButtonType.OUTLINED}
          onClick={filterOnClickHandler}
          elevation={ButtonElevation.NONE}
        >
          <ButtonIcon>
            {count ? (
              <Counter
                count={count}
                variant={filterIsOpen ? Variant.LIGHT : Variant.DARK}
                color={Color.PRIMARY}
              />
            ) : (
              <IconFilter size={18} />
            )}
          </ButtonIcon>
          <ButtonText>{t('filter', 'filter.title')}</ButtonText>
        </FilterButton>
      )}
      <Dropdown
        closeOnItemClick
        containerProps={{ 'data-test': 'SortOrderDropdown' }}
      >
        <DropdownToggle
          type={ButtonType.OUTLINED}
          showActiveIcon
          containerProps={{ 'data-test': 'DropdownToggle' }}
        >
          {dropdownSortSelected}
        </DropdownToggle>

        <DropdownContent placement={Direction.Down}>
          {dropdownSortOptions.map(option => {
            return (
              <DropdownItem
                data-test="DropdownOption"
                containerProps={{
                  'data-test-value': option.value,
                }}
                key={option.value}
                onClick={() => onSortChange(option.value)}
              >
                {option.name}
              </DropdownItem>
            );
          })}
        </DropdownContent>
      </Dropdown>

      <PanelHeaderButtons>
        <LayoutPicker mode={mode} onClick={layoutOnClickHandler} />
        <CompareProductsToggle
          active={compareProductSelection.isSelectingProducts}
          activate={compareProductSelection.startProductSelection}
          deactivate={abortProductSelectionFromCompareButton}
        />
      </PanelHeaderButtons>
    </PanelHeaderActions>
  );
};

interface CategoryHeaderActionsProps {
  mode: RenderMode;
  onSortChange: (key?: string | React.Key | undefined) => void;
  pageInfo: PageInfo | null | undefined;
  showHorizontalFilter: boolean;
  compareProductSelection: CompareProductsSelectionType;
}
